import LoadingScreen from 'components/LoadingScreen';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
} from 'reactstrap';

import {
  RecordStatusHistoryItem,
  RecordStatusHistoryStatus,
} from '../../types/bpaFindings.types';
import './RecordStatusHistory.css';

interface RecordStatusHistoryProps {
  items: RecordStatusHistoryItem[];
  title?: string;
  description?: string;
  isLoading?: boolean;
  onClose?: () => void;
  className?: string;
  emptyMessage?: string;
}

type StatusFilterKey = 'active' | 'fixed' | 'ignored' | 'unignored';
type SortOrder = 'desc' | 'asc';

const STATUS_FILTERS: Array<{
  key: StatusFilterKey;
  label: string;
  statuses: RecordStatusHistoryStatus[];
}> = [
  { key: 'active', label: 'Active', statuses: ['Active'] },
  { key: 'fixed', label: 'Fixed', statuses: ['Fixed'] },
  { key: 'ignored', label: 'Ignored', statuses: ['Ignored'] },
  { key: 'unignored', label: 'Unignored', statuses: ['Unignored'] },
];

const STATUS_STYLES: Record<
  RecordStatusHistoryStatus,
  { label: string; badgeColor: string; marker: string }
> = {
  Active: {
    label: 'Active',
    badgeColor: 'danger',
    marker: '#dc3545',
  },
  Fixed: {
    label: 'Fixed',
    badgeColor: 'success',
    marker: '#28a745',
  },
  Ignored: {
    label: 'Ignored',
    badgeColor: 'warning',
    marker: '#ffc107',
  },
  Unignored: {
    label: 'Unignored',
    badgeColor: 'danger',
    marker: '#dc3545',
  },
};

const ALL_STATUS_FILTER_KEYS = STATUS_FILTERS.map((filter) => filter.key);
const SYSTEM_SCAN_STATUSES = new Set<RecordStatusHistoryStatus>([
  'Active',
  'Fixed',
]);

const isSystemScanStatus = (status: RecordStatusHistoryStatus): boolean =>
  SYSTEM_SCAN_STATUSES.has(status);

const parseDateValue = (value: string | Date): number => {
  const date = value instanceof Date ? value : new Date(value);
  const timestamp = date.getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getFilterCount = (
  selectedStatuses: StatusFilterKey[],
  userFilter: string,
  sortOrder: SortOrder,
): number => {
  let count = 0;
  if (selectedStatuses.length !== STATUS_FILTERS.length) count += 1;
  if (userFilter.trim()) count += 1;
  if (sortOrder !== 'desc') count += 1;
  return count;
};

const RecordStatusHistory: React.FC<RecordStatusHistoryProps> = ({
  items,
  title = 'Status History',
  description = 'Showing all actions performed on this item.',
  isLoading = false,
  onClose,
  className = '',
  emptyMessage,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<StatusFilterKey[]>(
    ALL_STATUS_FILTER_KEYS,
  );
  const [userFilter, setUserFilter] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const activeFilterCount = getFilterCount(
    selectedStatuses,
    userFilter,
    sortOrder,
  );

  const filteredItems = useMemo(() => {
    const allowedStatuses = new Set(
      STATUS_FILTERS.filter((filter) =>
        selectedStatuses.includes(filter.key),
      ).flatMap((filter) => filter.statuses),
    );
    const normalizedUserFilter = userFilter.trim().toLowerCase();

    return items
      .filter((item) => {
        if (!allowedStatuses.has(item.status)) return false;
        if (!normalizedUserFilter) return true;
        if (isSystemScanStatus(item.status)) return false;
        return (item.performedBy || '')
          .toLowerCase()
          .includes(normalizedUserFilter);
      })
      .sort((a, b) => {
        const delta =
          parseDateValue(a.performedAt) - parseDateValue(b.performedAt);
        return sortOrder === 'asc' ? delta : -delta;
      });
  }, [items, selectedStatuses, sortOrder, userFilter]);

  const toggleStatusFilter = (filterKey: StatusFilterKey) => {
    setSelectedStatuses((current) => {
      if (current.includes(filterKey)) {
        return current.filter((key) => key !== filterKey);
      }
      return [...current, filterKey];
    });
  };

  const clearFilters = () => {
    setSelectedStatuses(ALL_STATUS_FILTER_KEYS);
    setUserFilter('');
    setSortOrder('desc');
  };

  const renderEmptyState = () => {
    if (isLoading) return null;
    const message =
      emptyMessage ||
      (items.length === 0
        ? 'No action history is available.'
        : 'No history entries match the selected filters.');

    return <div className="uah-empty-state">{message}</div>;
  };

  return (
    <section className={`uah-root ${className}`.trim()}>
      <div className="uah-header">
        <div>
          <h5 className="uah-title">{title}</h5>
        </div>
        <div className="uah-header-actions">
          <Dropdown
            isOpen={filtersOpen}
            toggle={() => setFiltersOpen((value) => !value)}
          >
            <DropdownToggle color="secondary" size="sm" caret>
              <i className="fa fa-filter mr-1" aria-hidden="true" />
              Filters
              {activeFilterCount > 0 && (
                <Badge color="primary" pill className="ml-2">
                  {activeFilterCount}
                </Badge>
              )}
            </DropdownToggle>
            <DropdownMenu right className="uah-filter-menu">
              <div className="uah-filter-section">
                <div className="uah-filter-heading">Status</div>
                {STATUS_FILTERS.map((filter) => (
                  <Label
                    key={filter.key}
                    check
                    className="uah-filter-option"
                    for={`uah-filter-${filter.key}`}
                  >
                    <Input
                      id={`uah-filter-${filter.key}`}
                      type="checkbox"
                      checked={selectedStatuses.includes(filter.key)}
                      onChange={() => toggleStatusFilter(filter.key)}
                    />
                    <span>{filter.label}</span>
                  </Label>
                ))}
              </div>
              <div className="uah-filter-section">
                <Label className="uah-filter-heading" for="uah-user-filter">
                  User
                </Label>
                <Input
                  id="uah-user-filter"
                  bsSize="sm"
                  value={userFilter}
                  placeholder="Filter by user"
                  onChange={(event) => setUserFilter(event.target.value)}
                />
              </div>
              <div className="uah-filter-section">
                <div className="uah-filter-heading">Sort</div>
                <Label check className="uah-filter-option" for="uah-sort-desc">
                  <Input
                    id="uah-sort-desc"
                    type="radio"
                    name="uah-sort-order"
                    checked={sortOrder === 'desc'}
                    onChange={() => setSortOrder('desc')}
                  />
                  <span>Newest first</span>
                </Label>
                <Label check className="uah-filter-option" for="uah-sort-asc">
                  <Input
                    id="uah-sort-asc"
                    type="radio"
                    name="uah-sort-order"
                    checked={sortOrder === 'asc'}
                    onChange={() => setSortOrder('asc')}
                  />
                  <span>Oldest first</span>
                </Label>
              </div>
              <Button
                color="link"
                size="sm"
                className="uah-clear-filters"
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear filters
              </Button>
            </DropdownMenu>
          </Dropdown>
          {onClose && (
            <Button
              color="link"
              size="sm"
              className="uah-close-button"
              onClick={onClose}
              aria-label="Close action history"
            >
              <i className="fa fa-times" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>

      <div className="uah-description">
        <span className="uah-info-icon" aria-hidden="true">
          <i className="fa fa-info" />
        </span>
        <span>{description}</span>
      </div>

      {isLoading && <LoadingScreen />}
      {!isLoading && filteredItems.length === 0 && renderEmptyState()}

      {!isLoading && filteredItems.length > 0 && (
        <div className="uah-timeline">
          {filteredItems.map((item) => {
            const style = STATUS_STYLES[item.status] || STATUS_STYLES.Active;
            const showPerformer = !isSystemScanStatus(item.status);
            const performedAt = moment(item.performedAt);
            const performedAtLabel = performedAt.isValid()
              ? performedAt.format('MMM D, YYYY hh:mm A')
              : '-';

            return (
              <article className="uah-entry" key={item.id}>
                <div className="uah-marker-column" aria-hidden="true">
                  <span
                    className="uah-marker"
                    style={{ backgroundColor: style.marker }}
                  />
                </div>
                <div className="uah-card">
                  <div className="uah-card-main">
                    <Badge
                      color={style.badgeColor}
                      pill
                      className="uah-status-badge"
                    >
                      {style.label}
                    </Badge>
                    <p className="uah-remarks">{item.remarks || '-'}</p>
                  </div>
                  <div className="uah-card-meta">
                    <time
                      className="uah-meta-row"
                      dateTime={String(item.performedAt)}
                    >
                      <i className="fas fa-calendar-alt" aria-hidden="true" />
                      <span>{performedAtLabel}</span>
                    </time>
                    {showPerformer && (
                      <span
                        className="uah-meta-row uah-performer"
                        title={item.performedBy || undefined}
                      >
                        <i className="fa fa-user" aria-hidden="true" />
                        <span>{item.performedBy || '-'}</span>
                      </span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default RecordStatusHistory;
